import { defineAsyncComponent, markRaw } from "vue";
import { $router, $session } from "dot-framework";
import global from "@/plugin/global";
import { routes as dotRoutes } from "@/router/route/index";
import { routes as kchfRoutes } from "@/tenant/KCHF/route/index";
import { routes as nlsRoutes } from "@/tenant/NLS/route/index";
import { routes as googleRoutes } from "@/tenant/GOOGLE/route/index";
import { externalUrls as dotExternalUrls } from "@/router/route/external";
import { externalUrls as nlsExternalUrls } from "@/tenant/NLS/route/external";
import { externalUrls as googleExternalUrls } from "@/tenant/GOOGLE/route/external";

/** 탭 제목·파비콘 등 미지정 시 */
const DEFAULT_DOCUMENT_TITLE = "Apps";
/** 기본 파비콘 = public/favicon.ico (NLS). GOOGLE만 settings.favicon 으로 분기 */
const DEFAULT_FAVICON = "favicon.ico";

function normalizeDomain(input) {
  if (!input) return "";
  let d = String(input).trim().toLowerCase();
  d = d.replace(/^https?:\/\//, "");
  const slashIdx = d.indexOf("/");
  if (slashIdx !== -1) d = d.slice(0, slashIdx);
  d = d.replace(/^localhost(?=[:/]|$)/, "127.0.0.1");
  return d;
}

// 주어진 domain이 포함된 settings 항목의 키(DOT/KCHF/NLS/GOOGLE 등)를 반환
function getCompCode(domain, fallback = "DOT") {
  const target = normalizeDomain(domain);
  const hit = Object.entries(settings).find(
    ([, v]) =>
      Array.isArray(v?.domains) &&
      v.domains.some((d) => normalizeDomain(d) === target)
  );
  return hit ? hit[0] : fallback;
}

/**
 * 업체별 설정 (COMP_CODE 키)
 * - appDocumentTitle?: 브라우저 탭 기본 제목 (없으면 DEFAULT_DOCUMENT_TITLE)
 * - favicon?: public 폴더 기준 파일명 (없으면 DEFAULT_FAVICON)
 */
const settings = {
  DOT: {
    routes: dotRoutes,
    domains: ["127.0.0.1:9070", "dev-apps.dotincorp.com", "apps-dotincorp.com"],
    externalUrls: dotExternalUrls,
    appDocumentTitle: "Dot Apps",
  },
  KCHF: {
    routes: kchfRoutes,
    domains: ["127.0.0.1:9070", "dev-apps.dotincorp.com", "apps-dotincorp.com"],
    externalUrls: dotExternalUrls,
    appDocumentTitle: "Dot Apps",
  },
  NLS: {
    routes: nlsRoutes,
    domains: ["127.0.0.1:9071", "dev-nls.apps-dotincorp.com", "nls.apps-dotincorp.com"],
    externalUrls: nlsExternalUrls,
    appDocumentTitle: "NLS Canvas",
  },
  GOOGLE: {
    routes: googleRoutes,
    domains: ["local.google.apps-dotincorp.com:9072", "dev-google.apps-dotincorp.com", "google.apps-dotincorp.com"],
    externalUrls: googleExternalUrls,
    appDocumentTitle: "Dot Apps",
    favicon: "favicon-google.ico",
  },
};

// ---------------------------------
// 레이아웃 설정
// ---------------------------------
const layouts = {
  // common layout
  BlankLayout: () => import("@/layout/BlankLayout.vue"),

  // DOT layout
  DefaultLayout: () => import("@/layout/apps/DefaultLayout.vue"),
  WideLayout: () => import("@/layout/WideLayout.vue"),
  CloudLayout: () => import("@/layout/apps/CloudLayout.vue"),
  EasyCloudLayout: () => import("@/layout/easy/EasyCloudLayout.vue"),

  // NLS layout
  NLSDefaultLayout: () => import("@/tenant/NLS/layout/apps/DefaultLayout.vue"),
  NLSWideLayout: () => import("@/tenant/NLS/layout/apps/WideLayout.vue"),
  NLSCloudLayout: () => import("@/tenant/NLS/layout/apps/CloudLayout.vue"),

  // KCHF layout
  KCHFDefaultLayout: () =>
    import("@/tenant/KCHF/layout/apps/DefaultLayout.vue"),
  KCHFWideLayout: () => import("@/tenant/KCHF/layout/apps/WideLayout.vue"),
  KCHFCloudLayout: () => import("@/tenant/KCHF/layout/apps/CloudLayout.vue"),

  // GOOGLE layout
  GoogleDefaultLayout: () => import("@/tenant/GOOGLE/layout/apps/DefaultLayout.vue"),
  GoogleWideLayout: () => import("@/tenant/GOOGLE/layout/apps/WideLayout.vue"),
  GoogleCloudLayout: () => import("@/tenant/GOOGLE/layout/apps/CloudLayout.vue"),
};


const $config = {
  // routes 조회
  getRoutes() {
    return settings[COMP_CODE]?.routes ?? [];
  },
  // layout 조회
  getLayout(name) {
    const loader = layouts[name];
    return markRaw(defineAsyncComponent(loader));
  },
  // mode별 외부 메뉴 링크 처리
  getExternalUrl(routePath) {
    const mode = (process.env.VUE_APP_MODE || "real").toLowerCase();
    const externalUrl = settings[COMP_CODE]?.externalUrls?.[mode]?.[routePath];
    console.log("* mode = " + mode + ", externalUrl = " + externalUrl);
    return externalUrl
      ? /^https?:\/\//i.test(externalUrl)
        ? externalUrl
        : `${mode === "local" ? "http" : "https"}://${externalUrl}`
      : "";
  },
};

// ---------------------------------
// 업체코드 설정
// ---------------------------------
const KCHF_USER = global.isKCHFUser();
const domain = $router.getDomain();
const COMP_CODE = KCHF_USER ? "KCHF" : getCompCode(domain);
const SSO_DOMAIN = $config.getExternalUrl("/account");
$session.setSsoDomain(SSO_DOMAIN);

function getAppDocumentTitle() {
  return settings[COMP_CODE]?.appDocumentTitle ?? DEFAULT_DOCUMENT_TITLE;
}

/** #app-favicon 의 href 를 현재 테넌트에 맞게 맞춤 */
function applyFavicon() {
  if (typeof document === "undefined") return;
  const base = process.env.BASE_URL || "/";
  const root = base.endsWith("/") ? base : `${base}/`;
  const file = settings[COMP_CODE]?.favicon ?? DEFAULT_FAVICON;
  const href = `${root}${file}`;

  let link = document.getElementById("app-favicon");
  if (!link) {
    link = document.createElement("link");
    link.id = "app-favicon";
    link.setAttribute("rel", "icon");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export { $config, COMP_CODE, getAppDocumentTitle, applyFavicon };
